import { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import {
  Sparkles, Upload, FileDown, RefreshCw, Plus, Trash2,
  ImageIcon, Send, X, Wand2, Settings, ListChecks, FileText,
  UploadCloud, FileImage, FileSpreadsheet, FileType, CheckCircle2, AlertCircle, Loader2,
  PlayCircle, RotateCcw
} from "lucide-react";
import "@/App.css";
import { Tour, useTour } from "@/Tour";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EMPTY_OFFER = {
  term: "12 months", funding_amount: 0, fixed_amount: 0, total_payback: 0,
  origination_fee: "2.50%", frequency: "Daily", apr: 0, mpr: 0,
  factor_rate: 0, interest_pct: 0, position: "1st",
};

const FREQ_OPTIONS = ["Daily", "Weekly", "Bi-Weekly", "Monthly"];
const POSITION_OPTIONS = ["1st", "2nd", "3rd", "4th"];

// Payments-per-year for each frequency (252 for daily = business days)
const PPY = { Daily:252, Weekly:52, "Bi-Weekly":26, "Semi-Monthly":24, Monthly:12 };

const THEME_SWATCHES = {
  pink:       { primary: "#3D0A2E", accent: "#FF2E78", accent2: "#FFC4DA" },
  navy:       { primary: "#0B1F3A", accent: "#FF6B2C", accent2: "#FFB400" },
  emerald:    { primary: "#0B3D2E", accent: "#10B981", accent2: "#FBBF24" },
  royal:      { primary: "#2E1065", accent: "#A855F7", accent2: "#FCD34D" },
  sunset:     { primary: "#7C2D12", accent: "#F97316", accent2: "#FBBF24" },
  monochrome: { primary: "#111827", accent: "#374151", accent2: "#9CA3AF" },
  ocean:      { primary: "#0C2D48", accent: "#00B4D8", accent2: "#90E0EF" },
  rose_gold:  { primary: "#4A2C36", accent: "#D4A373", accent2: "#F4C2C2" },
};

const THEME_LABEL = {
  pink: "Pink", navy: "Corporate Navy", emerald: "Emerald", royal: "Royal Purple",
  sunset: "Sunset", monochrome: "Monochrome", ocean: "Ocean", rose_gold: "Rose Gold",
};

const DECO_LABEL = {
  minimal: "Minimal Line",
  geometric: "Geometric",
  art_deco: "Art Deco",
  floral: "Floral",
  stars: "Sparkle Stars",
  confetti: "Confetti",
  ribbon_only: "Ribbon Banner",
  gift_box: "Gift Box",
  bow_ribbon: "Bow & Ribbon",
};

// preferred display order for the picker
const DECO_ORDER = [
  "minimal","geometric","art_deco","floral","stars","confetti","ribbon_only","gift_box","bow_ribbon"
];

function debounce(fn, ms = 600) {
  let t;
  return (...args) => { clearTimeout(t); t = setTimeout(() => fn(...args), ms); };
}

export default function App() {
  const [report, setReport] = useState(null);
  const [themes, setThemes] = useState([]);
  const [decorations, setDecorations] = useState([]);
  const [tab, setTab] = useState("design"); // design | offers | ai
  const tour = useTour();
  const [pdfVer, setPdfVer] = useState(0);
  const [building, setBuilding] = useState(false);
  const [chat, setChat] = useState([]);
  const [chatInput, setChatInput] = useState("");
  const [chatLoading, setChatLoading] = useState(false);
  const chatRef = useRef(null);

  // ----- bootstrap: load or create report -----
  useEffect(() => {
    (async () => {
      const [{ data: meta }] = await Promise.all([axios.get(`${API}/themes`)]);
      setThemes(meta.themes); setDecorations(meta.decorations);
      const existing = localStorage.getItem("report_id");
      if (existing) {
        try {
          const { data } = await axios.get(`${API}/reports/${existing}`);
          setReport(data);
          const ch = await axios.get(`${API}/reports/${existing}/chat`);
          setChat(ch.data.messages || []);
          return;
        } catch (_) { localStorage.removeItem("report_id"); }
      }
      const { data: created } = await axios.post(`${API}/reports`, { business_name: "Your Business" });
      setReport(created);
      localStorage.setItem("report_id", created.id);
    })();
  }, []);

  // ----- helpers: save patches with debounce -----
  const savePatch = useMemo(() => debounce(async (id, patch) => {
    const { data } = await axios.patch(`${API}/reports/${id}`, { patch });
    setReport(data);
    setPdfVer(v => v + 1);
  }, 500), []);

  const saveOffers = useMemo(() => debounce(async (id, offers) => {
    const { data } = await axios.put(`${API}/reports/${id}/offers`, { offers });
    setReport(data);
    setPdfVer(v => v + 1);
  }, 500), []);

  // ----- field updaters -----
  function updateField(key, value) {
    setReport(r => ({ ...r, [key]: value }));
    // when business name changes, also bump "prepared_for" if it's still the auto-default
    if (key === "business_name") {
      const old = report.business_name || "";
      const autoOld = `${old} - Ownership`;
      const isAuto = !report.prepared_for || report.prepared_for === autoOld;
      if (isAuto) {
        const newAuto = `${value} - Ownership`;
        setReport(r => ({ ...r, prepared_for: newAuto }));
        savePatch(report.id, { business_name: value, prepared_for: newAuto });
        return;
      }
    }
    savePatch(report.id, { [key]: value });
  }

  function updateOffer(i, key, value) {
    const offers = [...(report.offers || [])];
    const o = { ...offers[i] };

    // numeric normalization
    const numericKeys = ["funding_amount","fixed_amount","total_payback","apr","mpr","mrr","factor_rate","interest_pct"];
    let v = value;
    if (numericKeys.includes(key)) {
      v = parseFloat(String(value).replace(/[$,%]/g, "")) || 0;
    }
    o[key] = v;
    o._anchor = key;  // remember which field user just touched

    // ---- smart derivation pass ----
    smartDerive(o, key);

    offers[i] = o;
    setReport(r => ({ ...r, offers }));
    saveOffers(report.id, offers);
  }

  // helper: term → count of payments (bare number = count, "X months/weeks" = duration)
  function termToN(term, frequency) {
    const s = String(term || "").trim().toLowerCase();
    if (!s) return 0;
    const parts = s.replace(/,/g, "").split(/\s+/);
    const n = parseFloat(parts[0]);
    if (!isFinite(n) || n <= 0) return 0;
    const ppy = PPY[frequency] || 12;
    if (parts.length <= 1) return n;  // bare number = count of payments
    const unit = parts[1];
    if (unit.startsWith("mo"))                       return n * ppy / 12;
    if (unit.startsWith("wk") || unit.startsWith("week")) return n * ppy / 52;
    if (unit.startsWith("day") || unit === "d")      return n * ppy / 252;
    if (unit.startsWith("yr") || unit.startsWith("year")) return n * ppy;
    return n;
  }

  // helper: term → months (used for APR/MRR display)
  function termToMonths(term, frequency) {
    const N = termToN(term, frequency);
    const ppy = PPY[frequency] || 12;
    return ppy > 0 ? N * 12 / ppy : 0;
  }

  // Force a full recompute of derived values from canonical inputs.
  function recomputeOffer(o) {
    const next = { ...o };
    const fa = parseFloat(next.funding_amount) || 0;
    let pb = parseFloat(next.total_payback) || 0;
    let fr = parseFloat(next.factor_rate) || 0;
    let ip = parseFloat(next.interest_pct) || 0;

    // Prefer factor over interest when reconciling
    if (fr > 0) ip = +((fr - 1) * 100).toFixed(2);
    else if (ip > 0) fr = +(1 + ip / 100).toFixed(3);

    if (fa > 0 && fr > 0 && (!pb || Math.abs(pb - fa * fr) > 1)) {
      pb = +(fa * fr).toFixed(2);
      next.total_payback = pb;
    }
    if (fa > 0 && pb > 0) {
      fr = +(pb / fa).toFixed(3);
      ip = +((fr - 1) * 100).toFixed(2);
      next.factor_rate = fr;
    }
    next.interest_pct = ip;

    const N = termToN(next.term, next.frequency);
    if (pb > 0 && N > 0) {
      next.fixed_amount = +(pb / N).toFixed(2);
    }
    delete next._anchor;
    return next;
  }

  function recalcOffer(i) {
    const offers = [...(report.offers || [])];
    offers[i] = recomputeOffer(offers[i] || {});
    setReport(r => ({ ...r, offers }));
    saveOffers(report.id, offers);
  }

  function recalcAllOffers() {
    const offers = (report.offers || []).map(recomputeOffer);
    setReport(r => ({ ...r, offers }));
    saveOffers(report.id, offers);
  }

  // helper: parse term string to months (legacy duplicate kept removed)
  function smartDerive(o, edited) {
    const ppy = PPY[o.frequency] || 12;
    const N = termToN(o.term, o.frequency);

    const fa = parseFloat(o.funding_amount) || 0;
    let pb = parseFloat(o.total_payback) || 0;
    let pay = parseFloat(o.fixed_amount) || 0;
    let fr = parseFloat(o.factor_rate) || 0;
    let ip = parseFloat(o.interest_pct) || 0;

    // ---- Bidirectional interest_pct ⇄ factor_rate (always reconcile) ----
    if (edited === "interest_pct" && ip > 0) {
      fr = +(1 + ip / 100).toFixed(3);
      o.factor_rate = fr;
    } else if (edited === "factor_rate" && fr > 0) {
      ip = +((fr - 1) * 100).toFixed(2);
      o.interest_pct = ip;
    } else {
      // Reconcile silently if only one is set (e.g. user typed interest earlier
      // and is now editing term/freq/funded — we still need factor for derivations)
      if (fr > 0 && (!ip || Math.abs(ip - (fr - 1) * 100) > 0.1)) {
        ip = +((fr - 1) * 100).toFixed(2); o.interest_pct = ip;
      } else if (ip > 0 && (!fr || Math.abs(fr - (1 + ip / 100)) > 0.005)) {
        fr = +(1 + ip / 100).toFixed(3); o.factor_rate = fr;
      }
    }

    // 1. funded + (factor OR interest) → payback = funded × factor
    if (fa > 0 && fr > 0 && (!pb || edited === "factor_rate" || edited === "interest_pct" || edited === "funding_amount")) {
      pb = +(fa * fr).toFixed(2);
      o.total_payback = pb;
    }

    // 2. payback edited → factor + interest from payback / funded
    if (edited === "total_payback" && fa > 0 && pb > 0) {
      fr = +(pb / fa).toFixed(3);
      ip = +((fr - 1) * 100).toFixed(2);
      o.factor_rate = fr; o.interest_pct = ip;
    }

    // 3. funded edited and payback present (no factor) → factor + interest
    if (edited === "funding_amount" && fa > 0 && pb > 0 && (!fr || fr === 0)) {
      fr = +(pb / fa).toFixed(3);
      ip = +((fr - 1) * 100).toFixed(2);
      o.factor_rate = fr; o.interest_pct = ip;
    }

    // 4. payment edited + term + freq known → payback = payment × N
    if (edited === "fixed_amount" && pay > 0 && N > 0) {
      pb = +(pay * N).toFixed(2);
      o.total_payback = pb;
      if (fa > 0) {
        fr = +(pb / fa).toFixed(3);
        ip = +((fr - 1) * 100).toFixed(2);
        o.factor_rate = fr; o.interest_pct = ip;
      }
    }

    // 5. ANY non-payment input change → payment = payback / N
    if ((edited === "total_payback" || edited === "term" || edited === "frequency"
         || edited === "factor_rate" || edited === "interest_pct" || edited === "funding_amount")
        && pb > 0 && N > 0 && edited !== "fixed_amount") {
      pay = +(pb / N).toFixed(2);
      o.fixed_amount = pay;
    }

    // backfill factor + interest if missing
    if ((!fr || fr === 0) && fa > 0 && pb > 0) {
      fr = +(pb / fa).toFixed(3);
      o.factor_rate = fr;
      o.interest_pct = +((fr - 1) * 100).toFixed(2);
    }

    // Touch ppy so eslint sees it used (kept for potential future logic)
    void ppy;
    delete o._anchor;
    return o;
  }

  function addOffer() {
    const offers = [...(report.offers || []), { ...EMPTY_OFFER }];
    setReport(r => ({ ...r, offers }));
    saveOffers(report.id, offers);
  }

  function removeOffer(i) {
    const offers = (report.offers || []).filter((_, k) => k !== i);
    setReport(r => ({ ...r, offers }));
    saveOffers(report.id, offers);
  }

  // ----- uploads -----
  const [extracting, setExtracting] = useState(false);
  const [extractResult, setExtractResult] = useState(null); // {files:[...], added, count}

  async function extractFiles(fileList, mode = "replace") {
    const files = Array.from(fileList || []);
    if (!files.length || !report) return;
    setExtracting(true);
    setExtractResult(null);
    const fd = new FormData();
    files.forEach(f => fd.append("files", f));
    try {
      const { data } = await axios.post(
        `${API}/reports/${report.id}/extract-offers?mode=${mode}`, fd,
        { timeout: 120000 }
      );
      setReport(r => ({ ...r, offers: data.offers }));
      setPdfVer(v => v + 1);
      setExtractResult(data);
      setTab("offers");
    } catch (err) {
      setExtractResult({ error: err.response?.data?.detail || err.message });
    } finally {
      setExtracting(false);
    }
  }

  async function handleLogo(e) {
    const f = e.target.files?.[0]; if (!f) return;
    const fd = new FormData(); fd.append("file", f);
    const { data } = await axios.post(`${API}/reports/${report.id}/upload-logo`, fd);
    setReport(r => ({ ...r, logo_image: data.logo_image }));
    setPdfVer(v => v + 1);
    e.target.value = "";
  }

  async function removeLogo() {
    await axios.delete(`${API}/reports/${report.id}/logo`);
    setReport(r => ({ ...r, logo_image: null }));
    setPdfVer(v => v + 1);
  }

  // ----- chat -----
  async function sendChat() {
    const msg = chatInput.trim();
    if (!msg || !report) return;
    setChat(c => [...c, { role: "user", text: msg }]);
    setChatInput("");
    setChatLoading(true);
    try {
      const { data } = await axios.post(`${API}/reports/${report.id}/chat`, { message: msg });
      setChat(c => [...c, { role: "assistant", text: data.summary }]);
      if (data.report) {
        setReport(data.report);
        setPdfVer(v => v + 1);
      }
    } catch (err) {
      setChat(c => [...c, { role: "assistant", text: "Sorry — I hit an error. Try rephrasing." }]);
    } finally {
      setChatLoading(false);
      setTimeout(() => chatRef.current?.scrollTo(0, chatRef.current.scrollHeight), 100);
    }
  }

  async function undoLastAiEdit() {
    if (!report || !report.undo_available || chatLoading) return;
    setChatLoading(true);
    try {
      const { data } = await axios.post(`${API}/reports/${report.id}/undo`);
      if (data.report) {
        setReport(data.report);
        setPdfVer(v => v + 1);
      }
      setChat(c => [...c, { role: "assistant", text: data.summary || "Reverted last AI edit." }]);
    } catch (err) {
      const detail = err?.response?.data?.detail || "Couldn't undo — nothing to revert.";
      setChat(c => [...c, { role: "assistant", text: detail }]);
    } finally {
      setChatLoading(false);
      setTimeout(() => chatRef.current?.scrollTo(0, chatRef.current.scrollHeight), 100);
    }
  }

  function newReport() {
    if (!window.confirm("Start a fresh report? Current work will be replaced.")) return;
    localStorage.removeItem("report_id");
    window.location.reload();
  }

  function downloadPdf() {
    if (!report) return;
    window.open(`${API}/reports/${report.id}/pdf?download=1&v=${pdfVer}`, "_blank");
  }

  if (!report) {
    return (
      <div style={{display:"flex",alignItems:"center",justifyContent:"center",height:"100vh"}} data-testid="loading-screen">
        <div className="spinner" /> <span style={{marginLeft:10}}>Loading…</span>
      </div>
    );
  }

  const pdfUrl = `${API}/reports/${report.id}/pdf?v=${pdfVer}#toolbar=0&navpanes=0`;

  return (
    <div className="App" data-testid="app-root">
      {/* Top bar */}
      <header style={{
        position:"sticky", top:0, zIndex:20,
        backdropFilter:"blur(12px)", background:"rgba(11,14,19,0.7)",
        borderBottom:"1px solid var(--line)",
      }}>
        <div style={{
          display:"flex", alignItems:"center", justifyContent:"space-between",
          padding:"0.8rem 1.3rem", gap:"1rem",
        }}>
          <div style={{display:"flex", alignItems:"center", gap:"0.7rem"}}>
            <div style={{
              width: 36, height: 36, borderRadius: 8,
              background: "#1E3A8A",
              display:"flex", alignItems:"center", justifyContent:"center",
              border: "1px solid rgba(59,130,246,0.4)",
            }}>
              <FileText size={18} color="#93C5FD" strokeWidth={2.2} />
            </div>
            <div>
              <div style={{fontSize:"1rem", lineHeight:1, fontWeight:700, color:"var(--ink)"}}>
                Offer Brochure Builder
              </div>
              <div style={{fontSize:"0.72rem", color:"var(--muted)", marginTop:3}}>
                Upload your offers, export a polished PDF
              </div>
            </div>
          </div>
          <div style={{display:"flex", gap:"0.5rem", alignItems:"center"}}>
            <button className="btn btn-ghost" onClick={tour.start} data-testid="restart-tour-btn" title="Replay the guided tour">
              <PlayCircle size={14}/> Tour
            </button>
            <button className="btn btn-ghost" onClick={newReport} data-testid="new-report-btn">
              <RefreshCw size={14}/> New report
            </button>
            <button className="btn btn-primary" onClick={downloadPdf} data-testid="download-pdf-btn">
              <FileDown size={16}/> Download PDF
            </button>
          </div>
        </div>
      </header>

      {/* Main 3-column layout */}
      <main style={{
        display:"grid", gridTemplateColumns:"380px 1fr 420px",
        gap:"1.1rem", padding:"1.1rem", maxWidth: 1700, margin:"0 auto",
        alignItems:"start",
      }}>
        {/* LEFT - configuration */}
        <section className="card" style={{padding:"1rem", maxHeight:"calc(100vh - 110px)", overflowY:"auto"}} data-testid="config-panel">
          <div className="tab-row" style={{marginBottom:"0.9rem"}}>
            <button className={tab==="design"?"active":""} onClick={()=>setTab("design")} data-testid="tab-design">
              <Wand2 size={14} style={{display:"inline",marginRight:4,verticalAlign:"-3px"}}/> Design
            </button>
            <button className={tab==="offers"?"active":""} onClick={()=>setTab("offers")} data-testid="tab-offers">
              <ListChecks size={14} style={{display:"inline",marginRight:4,verticalAlign:"-3px"}}/> Offers
            </button>
            <button className={tab==="ai"?"active":""} onClick={()=>setTab("ai")} data-testid="tab-ai">
              <Sparkles size={14} style={{display:"inline",marginRight:4,verticalAlign:"-3px"}}/> AI Editor
            </button>
          </div>

          {tab === "design" && <DesignPanel
            report={report} updateField={updateField}
            themes={themes} decorations={decorations}
            handleLogo={handleLogo} removeLogo={removeLogo}
          />}

          {tab === "offers" && <OffersPanel
            report={report} updateOffer={updateOffer}
            addOffer={addOffer} removeOffer={removeOffer}
            recalcOffer={recalcOffer} recalcAllOffers={recalcAllOffers}
            extractFiles={extractFiles}
            extracting={extracting} extractResult={extractResult}
            updateField={updateField}
          />}

          {tab === "ai" && <AiHelp />}
        </section>

        {/* CENTER - PDF preview */}
        <section className="card" style={{padding:"0.6rem", height:"calc(100vh - 110px)", display:"flex", flexDirection:"column"}} data-testid="preview-panel">
          <div style={{display:"flex", alignItems:"center", justifyContent:"space-between", padding:"0.2rem 0.5rem 0.5rem"}}>
            <div style={{display:"flex", alignItems:"center", gap:"0.5rem"}}>
              <span className="chip" data-testid="theme-chip">{THEME_LABEL[report.theme] || report.theme}</span>
              <span className="chip" style={{background:"rgba(251,191,36,0.12)", color:"#FCD34D", borderColor:"rgba(251,191,36,0.3)"}} data-testid="deco-chip">
                {DECO_LABEL[report.decoration] || report.decoration}
              </span>
              <span style={{color:"var(--muted)", fontSize:"0.78rem"}}>{(report.offers||[]).length} offers</span>
            </div>
            <button className="btn btn-ghost" onClick={() => setPdfVer(v=>v+1)} data-testid="refresh-pdf-btn">
              <RefreshCw size={14}/> Refresh
            </button>
          </div>
          <iframe
            key={pdfVer}
            src={pdfUrl}
            title="brochure preview"
            data-testid="pdf-preview-iframe"
            style={{flex:1, border:"1px solid var(--line)", borderRadius:12, background:"#1a0f1f"}}
          />
        </section>

        {/* RIGHT - AI chat */}
        <section className="card" style={{padding:"1rem", height:"calc(100vh - 110px)", display:"flex", flexDirection:"column"}} data-testid="chat-panel">
          <div style={{display:"flex", alignItems:"center", gap:"0.55rem", marginBottom:"0.5rem"}}>
            <div style={{
              width:30,height:30,borderRadius:8,
              background:"rgba(59,130,246,0.15)",
              border:"1px solid rgba(59,130,246,0.35)",
              display:"flex",alignItems:"center",justifyContent:"center"
            }}>
              <Sparkles size={14} color="#93C5FD"/>
            </div>
            <div>
              <div style={{fontWeight:700,fontSize:"0.95rem"}}>AI Editor</div>
              <div style={{fontSize:"0.72rem",color:"var(--muted)"}}>Powered by Claude Opus 4.6</div>
            </div>
          </div>

          <div ref={chatRef} style={{
            flex:1, overflowY:"auto", padding:"0.5rem",
            display:"flex", flexDirection:"column", gap:"0.5rem",
            background:"rgba(0,0,0,0.18)", borderRadius:12, border:"1px solid var(--line)",
          }} data-testid="chat-scroll">
            {chat.length === 0 && (
              <div style={{padding:"1rem", color:"var(--muted)", fontSize:"0.85rem"}}>
                <div style={{marginBottom:"0.5rem", fontWeight:700, color:"var(--ink-2)"}}>Try asking:</div>
                {[
                  "Make it navy and minimal",
                  "Use the geometric decoration",
                  "Highlight offer 13 as recommended",
                  "Change the tagline to 'Capital that grows with you'",
                  "Hide offer 11",
                  "Set my email to joe@example.com",
                ].map(t => (
                  <button key={t} className="btn btn-ghost" style={{margin:"3px",fontSize:"0.75rem"}}
                          onClick={()=>setChatInput(t)} data-testid={`example-${t.slice(0,10)}`}>
                    {t}
                  </button>
                ))}
              </div>
            )}
            {chat.map((m, i) => (
              <div key={i} className={`bubble ${m.role==="user"?"user":"ai"}`} data-testid={`chat-${m.role}-${i}`}>
                {m.text}
              </div>
            ))}
            {chatLoading && (
              <div className="bubble ai" data-testid="chat-loading">
                <span className="spinner" style={{display:"inline-block", verticalAlign:"-3px", marginRight:6}}/> thinking…
              </div>
            )}
          </div>

          <div style={{display:"flex", gap:"0.4rem", marginTop:"0.6rem", alignItems:"stretch"}}>
            <button
              className="btn btn-ghost"
              onClick={undoLastAiEdit}
              disabled={!report?.undo_available || chatLoading}
              title={report?.undo_available ? "Undo last AI edit" : "No AI edits to undo"}
              data-testid="undo-ai-btn"
              style={{ opacity: report?.undo_available ? 1 : 0.45, alignSelf:"stretch" }}
            >
              <RotateCcw size={14}/> Undo
            </button>
            <textarea
              className="textarea"
              rows={2}
              placeholder="Ask the AI to edit your brochure…"
              value={chatInput}
              onChange={e=>setChatInput(e.target.value)}
              onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendChat(); } }}
              data-testid="chat-input"
            />
            <button className="btn btn-primary" onClick={sendChat} disabled={chatLoading || !chatInput.trim()} data-testid="chat-send-btn">
              <Send size={16}/>
            </button>
          </div>
        </section>
      </main>

      <Tour
        active={tour.active}
        step={tour.step}
        onNext={tour.next}
        onPrev={tour.prev}
        onSkip={tour.finish}
        onFinish={tour.finish}
        onTabChange={(t) => setTab(t)}
      />
    </div>
  );
}


function DesignPanel({ report, updateField, themes, decorations, handleLogo, removeLogo }) {
  return (
    <div data-testid="design-panel">
      <SectionTitle icon={<Settings size={14}/>} title="Business" />
      <Field label="Business name">
        <input className="input" value={report.business_name || ""} data-testid="input-business-name"
               onChange={e => updateField("business_name", e.target.value)} />
      </Field>
      <Field label="Tagline (under business name)">
        <input className="input" value={report.tagline || ""} data-testid="input-tagline"
               onChange={e => updateField("tagline", e.target.value)} />
      </Field>
      <Field label="Sub-headline (under FUNDING PROPOSAL)">
        <input className="input" value={report.subhead || ""} data-testid="input-subhead"
               onChange={e => updateField("subhead", e.target.value)} />
      </Field>
      <Field label="Prepared for (on cover page)">
        <input className="input" value={report.prepared_for || ""} data-testid="input-prepared-for"
               onChange={e => updateField("prepared_for", e.target.value)} />
      </Field>
      <Field label="Page 2 intro paragraph (one thought per line)">
        <textarea className="textarea" rows={5}
                  value={(report.intro_lines && report.intro_lines.length) ? report.intro_lines.join("\n") : ""}
                  placeholder={`We've structured these funding offers to match your cash-flow rhythm.\nEach option lays out term, payment, total payback, fees, and APR.\nPick the structure that fits your business — not ours.`}
                  data-testid="input-intro-lines"
                  onChange={e => updateField("intro_lines", e.target.value.split("\n"))} />
      </Field>

      <SectionTitle icon={<Settings size={14}/>} title="Prepared by" />
      <Field label="Your name">
        <input className="input" value={report.prepared_by_name || ""} data-testid="input-rep-name"
               onChange={e => updateField("prepared_by_name", e.target.value)} />
      </Field>
      <div style={{display:"grid", gridTemplateColumns:"1fr 1fr", gap:8}}>
        <Field label="Phone">
          <input className="input" value={report.prepared_by_phone || ""} data-testid="input-rep-phone"
                 onChange={e => updateField("prepared_by_phone", e.target.value)} />
        </Field>
        <Field label="Email">
          <input className="input" value={report.prepared_by_email || ""} data-testid="input-rep-email"
                 onChange={e => updateField("prepared_by_email", e.target.value)} />
        </Field>
      </div>

      <SectionTitle icon={<ImageIcon size={14}/>} title="Logo" />
      <div style={{display:"flex", alignItems:"center", gap:10, marginBottom:14}}>
        <div style={{
          width:54, height:54, borderRadius:10,
          border:"1px solid var(--line)",
          background: report.logo_image
            ? `url(${API}/reports/${report.id}/logo?v=${(report.updated_at||"").length}) center/cover no-repeat #fff`
            : "#1E3A8A",
          display:"flex", alignItems:"center", justifyContent:"center", color:"#93C5FD",
          fontWeight:800, fontSize:"1rem", overflow:"hidden",
        }}>
          {report.logo_image ? null : (report.logo_text || "EHS").slice(0,3).toUpperCase()}
        </div>
        <div style={{flex:1}}>
          <Field label="Monogram (used when no image)">
            <input className="input" value={report.logo_text || ""} data-testid="input-logo-text"
                   onChange={e => updateField("logo_text", e.target.value.toUpperCase().slice(0,3))} />
          </Field>
        </div>
      </div>
      <div style={{display:"flex", gap:6, marginBottom:12}}>
        <label className="btn btn-ghost" style={{cursor:"pointer"}} data-testid="upload-logo-btn">
          <Upload size={14}/> Upload logo image
          <input type="file" accept="image/*" onChange={handleLogo} hidden />
        </label>
        {report.logo_image && (
          <button className="btn btn-ghost" onClick={removeLogo} data-testid="remove-logo-btn">
            <X size={14}/> Remove
          </button>
        )}
      </div>

      <SectionTitle icon={<Wand2 size={14}/>} title="Theme" />
      <div style={{display:"grid", gridTemplateColumns:"repeat(4, 1fr)", gap:8, marginBottom:14}}>
        {Object.keys(THEME_SWATCHES).map(name => {
          const sw = THEME_SWATCHES[name];
          const active = report.theme === name;
          return (
            <div key={name}
                 className={`swatch ${active?"active":""}`}
                 data-testid={`theme-${name}`}
                 onClick={() => updateField("theme", name)}>
              <div style={{position:"absolute", inset:0, background: sw.primary}}/>
              <div className="bar" style={{position:"absolute", left:0, right:0, bottom:0, background: sw.accent}}/>
              <div style={{position:"absolute", left:6, top:6, width:14, height:14, borderRadius:"50%", background: sw.accent2}}/>
              <div style={{position:"absolute", left:0, right:0, bottom:0, padding:"3px 6px", fontSize:"0.66rem", fontWeight:700, color:"white", textTransform:"uppercase", letterSpacing:"0.04em"}}>{THEME_LABEL[name]}</div>
            </div>
          );
        })}
      </div>

      <SectionTitle icon={<Sparkles size={14}/>} title="Decoration concept" />
      <div style={{display:"grid", gridTemplateColumns:"repeat(3, 1fr)", gap:8, marginBottom:14}}>
        {DECO_ORDER.filter(d => decorations.includes(d)).map(d => (
          <div key={d}
               className={`deco-tile ${report.decoration===d?"active":""}`}
               data-testid={`deco-${d}`}
               onClick={() => updateField("decoration", d)}>
            <DecoIcon name={d} theme={report.theme}/>
            <div className="name">{DECO_LABEL[d] || d}</div>
          </div>
        ))}
      </div>

      <SectionTitle icon={<ListChecks size={14}/>} title="Closing page" />
      <Field label="Closing headline">
        <input className="input" value={report.cta_title || ""} data-testid="input-cta-title"
               onChange={e => updateField("cta_title", e.target.value)} />
      </Field>
      <Field label="Closing body (one thought per line)">
        <textarea className="textarea" rows={3} value={report.cta_body || ""} data-testid="input-cta-body"
                  onChange={e => updateField("cta_body", e.target.value)} />
      </Field>

      <StipsEditor report={report} updateField={updateField} />

      <Field label="Recommended offer #">
        <input
          className="input" type="number" min="0"
          placeholder="0 = none"
          value={report.recommended_offer_index || ""}
          data-testid="input-recommended"
          onChange={e => {
            const n = parseInt(e.target.value, 10);
            updateField("recommended_offer_index", Number.isFinite(n) && n > 0 ? n : null);
          }}
        />
      </Field>
    </div>
  );
}


function StipsEditor({ report, updateField }) {
  const stips = report.stips || [];
  const showStips = report.show_stips !== false;
  const [newLabel, setNewLabel] = useState("");

  function toggle(id) {
    const next = stips.map(s => s.id === id ? { ...s, checked: !s.checked } : s);
    updateField("stips", next);
  }
  function remove(id) {
    const next = stips.filter(s => s.id !== id);
    updateField("stips", next);
  }
  function add() {
    const label = newLabel.trim();
    if (!label) return;
    const id = (typeof crypto !== "undefined" && crypto.randomUUID) ? crypto.randomUUID() : `c-${Date.now()}`;
    const next = [...stips, { id, label, checked: true, custom: true }];
    updateField("stips", next);
    setNewLabel("");
  }

  return (
    <div style={{marginTop:14, marginBottom:14}} data-testid="stips-editor">
      <div style={{display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:8}}>
        <SectionTitle icon={<ListChecks size={14}/>} title="Documents needed (stips)" />
        <label style={{display:"inline-flex", alignItems:"center", gap:6, fontSize:"0.75rem", color:"var(--muted)"}}>
          <input type="checkbox" checked={showStips}
                 onChange={e => updateField("show_stips", e.target.checked)}
                 data-testid="toggle-show-stips" />
          Show on PDF
        </label>
      </div>

      <div style={{
        background:"rgba(0,0,0,0.18)", border:"1px solid var(--line)", borderRadius:10,
        padding:"0.55rem 0.6rem", maxHeight:230, overflowY:"auto",
        opacity: showStips ? 1 : 0.55,
      }}>
        {stips.length === 0 && (
          <div style={{padding:"0.4rem", color:"var(--muted)", fontSize:"0.8rem"}}>No items. Add one below.</div>
        )}
        {stips.map(s => (
          <div key={s.id} style={{display:"flex", alignItems:"center", gap:8, padding:"4px 2px"}}
               data-testid={`stip-${s.id}`}>
            <input type="checkbox" checked={!!s.checked} onChange={() => toggle(s.id)}
                   data-testid={`stip-toggle-${s.id}`} />
            <span style={{flex:1, fontSize:"0.83rem", color: s.checked ? "var(--ink)" : "var(--muted)"}}>
              {s.label}
              {s.custom && <span style={{marginLeft:6, fontSize:"0.65rem", color:"var(--muted)", fontWeight:600}}>· CUSTOM</span>}
            </span>
            {s.custom && (
              <button className="btn btn-ghost btn-icon" onClick={() => remove(s.id)}
                      title="Remove this custom stip" data-testid={`stip-remove-${s.id}`}>
                <Trash2 size={13}/>
              </button>
            )}
          </div>
        ))}
      </div>

      <div style={{display:"flex", gap:6, marginTop:8}}>
        <input className="input" placeholder="Add custom stip (e.g. PG personal credit auth)"
               value={newLabel}
               onChange={e => setNewLabel(e.target.value)}
               onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); add(); } }}
               data-testid="stip-add-input" />
        <button className="btn btn-primary" onClick={add} disabled={!newLabel.trim()}
                data-testid="stip-add-btn">
          <Plus size={14}/>
        </button>
      </div>
    </div>
  );
}


function OffersPanel({ report, updateOffer, addOffer, removeOffer, recalcOffer, recalcAllOffers, extractFiles, extracting, extractResult, updateField }) {
  const offers = report.offers || [];
  const [dragOver, setDragOver] = useState(false);
  const [appendMode, setAppendMode] = useState(true);  // default: ADD to existing offers
  const fileInputRef = useRef(null);

  function onDrop(e) {
    e.preventDefault(); setDragOver(false);
    const fl = e.dataTransfer?.files;
    if (fl && fl.length) extractFiles(fl, appendMode ? "append" : "replace");
  }

  return (
    <div data-testid="offers-panel">
      {/* DROP ZONE */}
      <div
        onDragOver={e => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
        onClick={() => fileInputRef.current?.click()}
        data-testid="drop-zone"
        style={{
          border: `1.5px dashed ${dragOver ? "var(--accent)" : "var(--line-2)"}`,
          background: dragOver ? "rgba(59,130,246,0.08)" : "rgba(255,255,255,0.02)",
          borderRadius: 12,
          padding: "1.4rem 1rem",
          textAlign: "center",
          cursor: "pointer",
          transition: "all 120ms ease",
          marginBottom: 10,
        }}>
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept=".csv,text/csv,image/png,image/jpeg,image/jpg,image/webp,image/gif,application/pdf"
          onChange={e => {
            const files = Array.from(e.target.files || []);   // snapshot the list first
            e.target.value = "";                              // then reset so same file can be re-picked
            if (files.length) extractFiles(files, appendMode ? "append" : "replace");
          }}
          hidden
          data-testid="drop-zone-input"
        />
        {extracting ? (
          <div style={{display:"flex", alignItems:"center", justifyContent:"center", gap:10, color:"var(--ink)"}}>
            <Loader2 size={20} className="spin"/>
            <div>
              <div style={{fontWeight:700}}>Reading your offers with AI…</div>
              <div style={{fontSize:"0.75rem", color:"var(--muted)"}}>This usually takes 5–15 seconds per file.</div>
            </div>
          </div>
        ) : (
          <>
            <UploadCloud size={28} color="#93C5FD" style={{margin:"0 auto"}}/>
            <div style={{fontWeight:700, marginTop:8, color:"var(--ink)"}}>
              Drop offers here, or <span style={{color:"var(--accent-2)", textDecoration:"underline"}}>click to browse</span>
            </div>
            <div style={{fontSize:"0.75rem", color:"var(--muted)", marginTop:6}}>
              CSV, PNG, JPG, WEBP, or PDF — multiple files OK. The AI reads images & PDFs.
            </div>
          </>
        )}
      </div>

      <div style={{display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:10, gap:8, flexWrap:"wrap"}}>
        <label style={{display:"flex", alignItems:"center", gap:6, fontSize:"0.78rem", color:"var(--muted)", cursor:"pointer"}}>
          <input type="checkbox" checked={!appendMode} onChange={e => setAppendMode(!e.target.checked)} data-testid="append-mode-toggle"/>
          Replace existing offers (default: add to list)
        </label>
        <div style={{display:"flex", gap:6, alignItems:"center"}}>
          {(report.offers || []).length > 0 && (
            <button className="btn btn-ghost" onClick={recalcAllOffers} data-testid="recalc-all-btn"
                    title="Force-recompute payment, factor and APR for every offer from funded + payback + term + frequency">
              <RotateCcw size={14}/> Recalc all
            </button>
          )}
          <button className="btn btn-ghost" onClick={addOffer} data-testid="add-offer-btn">
            <Plus size={14}/> Add offer manually
          </button>
        </div>
      </div>

      {extractResult && (
        <div style={{marginBottom:10, padding:"0.6rem 0.8rem", border:"1px solid var(--line)", borderRadius:8,
                     background: extractResult.error ? "rgba(239,68,68,0.08)" : "rgba(34,197,94,0.06)"}}>
          {extractResult.error ? (
            <div style={{display:"flex", alignItems:"center", gap:8, color:"#FCA5A5", fontSize:"0.82rem"}}>
              <AlertCircle size={16}/> {extractResult.error}
            </div>
          ) : (
            <div data-testid="extract-result">
              <div style={{display:"flex", alignItems:"center", gap:8, color:"#86EFAC", fontSize:"0.85rem", fontWeight:700}}>
                <CheckCircle2 size={16}/> Added {extractResult.added} offer{extractResult.added===1?"":"s"} ({extractResult.count} total)
              </div>
              <div style={{fontSize:"0.74rem", color:"var(--muted)", marginTop:4}}>
                {extractResult.files?.map((f,i) => (
                  <div key={i}>
                    <FileType size={11} style={{display:"inline", verticalAlign:"-2px", marginRight:4}}/>
                    <span style={{color:"var(--ink-2)"}}>{f.file}</span> — {f.error ? <span style={{color:"#FCA5A5"}}>{f.error}</span> : <>{f.count} offer{f.count===1?"":"s"} via {f.method}</>}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {offers.length === 0 && !extracting && (
        <div style={{padding:"1.4rem", border:"1px dashed var(--line)", borderRadius:12, textAlign:"center", color:"var(--muted)", fontSize:"0.85rem"}}>
          No offers yet. Drop screenshots / PDFs / CSV above, or use <b>Add offer manually</b>.
        </div>
      )}

      {/* OFFER CARDS (MCA-friendly) */}
      <div style={{display:"flex", flexDirection:"column", gap:10}}>
        {offers.map((o, i) => (
          <OfferCardEditor key={i} index={i} offer={o}
                           onChange={(k,v)=>updateOffer(i,k,v)}
                           onRemove={()=>removeOffer(i)}
                           onRecalc={()=>recalcOffer(i)} />
        ))}
      </div>

      {/* FIELD VISIBILITY TOGGLES */}
      <div style={{marginTop:16, padding:"0.85rem 1rem", border:"1px solid var(--line)", borderRadius:12, background:"rgba(255,255,255,0.02)"}}>
        <div style={{fontSize:"0.72rem", fontWeight:800, letterSpacing:"0.08em", color:"var(--ink-2)", textTransform:"uppercase", marginBottom:8, display:"flex", alignItems:"center", gap:6}}>
          <ListChecks size={14}/> Fields to show on the PDF
        </div>
        <div style={{display:"grid", gridTemplateColumns:"1fr 1fr", gap:6}}>
          {[
            {key:"show_term",      label:"Term"},
            {key:"show_funding",   label:"Funded amount"},
            {key:"show_payment",   label:"Payment"},
            {key:"show_frequency", label:"Frequency"},
            {key:"show_payback",   label:"Total payback"},
            {key:"show_position",  label:"Position"},
            {key:"show_factor",    label:"Factor rate"},
            {key:"show_fee",       label:"Origination fee"},
            {key:"show_apr",       label:"APR %"},
            {key:"show_mrr",       label:"MRR %"},
          ].map(t => {
            const on = report[t.key] !== false;
            return (
              <button key={t.key}
                      data-testid={`toggle-${t.key}`}
                      onClick={() => updateField(t.key, !on)}
                      style={{
                        cursor:"pointer", textAlign:"left",
                        padding:"0.5rem 0.65rem", borderRadius:8,
                        border:`1px solid ${on?"rgba(59,130,246,0.5)":"var(--line)"}`,
                        background: on?"rgba(59,130,246,0.10)":"transparent",
                        color: on?"var(--ink)":"var(--muted)",
                        fontFamily:"inherit", fontSize:"0.78rem", fontWeight:600,
                        display:"flex", alignItems:"center", gap:7,
                      }}>
                <span style={{
                  minWidth:14, width:14, height:14, borderRadius:4,
                  background: on?"var(--accent)":"transparent",
                  border:`1px solid ${on?"var(--accent)":"var(--line-2)"}`,
                  display:"inline-flex", alignItems:"center", justifyContent:"center",
                  color:"white", fontSize:11, fontWeight:900,
                }}>{on?"✓":""}</span>
                {t.label}
              </button>
            );
          })}
        </div>
        <div style={{fontSize:"0.7rem", color:"var(--muted)", marginTop:8}}>
          Unchecked fields are hidden across the entire PDF — comparison table, offer cards, page-2 summary stats, and the featured pill.
        </div>
      </div>
    </div>
  );
}


function OfferCardEditor({ index, offer, onChange, onRemove, onRecalc }) {
  // auto-compute factor rate + APR + MPR live (matches backend formula)
  const fa = parseFloat(offer.funding_amount) || 0;
  const tp = parseFloat(offer.total_payback) || 0;
  const pay = parseFloat(offer.fixed_amount) || 0;
  const computedFactor = (parseFloat(offer.factor_rate) > 0)
    ? parseFloat(offer.factor_rate)
    : (fa > 0 ? (tp / fa) : 0);

  // months from term string — bare number = count of payments at the freq
  const months = (() => {
    const s = String(offer.term || "").trim().toLowerCase().split(/\s+/);
    const n = parseFloat(s[0]); if (!isFinite(n) || n <= 0) return 0;
    const ppy = PPY[offer.frequency] || 12;
    if (s.length <= 1) return n * 12 / ppy;  // bare number = count of payments
    const u = s[1];
    if (u.startsWith("mo")) return n;
    if (u.startsWith("wk") || u.startsWith("week")) return n * 12/52;
    if (u.startsWith("day") || u === "d") return n * 12/252;
    if (u.startsWith("yr") || u.startsWith("year")) return n * 12;
    return n;
  })();
  let m = months;
  if (m <= 0 && pay > 0 && tp > 0) {
    const ppy = PPY[offer.frequency] || 12;
    m = (tp / pay) * 12 / ppy;
  }
  // FLAT APR (broker-friendly): just the interest %, NOT annualized
  const apr = (fa > 0 && tp > 0) ? ((tp/fa - 1) * 100) : 0;
  const mrr = m > 0 ? apr / m : 0;

  return (
    <div className="card" style={{padding:"0.9rem 1rem", position:"relative"}} data-testid={`offer-card-${index}`}>
      <div style={{display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:10}}>
        <div style={{display:"flex", alignItems:"center", gap:8, flexWrap:"wrap"}}>
          <div style={{
            width:28, height:28, borderRadius:6,
            background:"rgba(59,130,246,0.15)", border:"1px solid rgba(59,130,246,0.35)",
            display:"flex", alignItems:"center", justifyContent:"center",
            fontWeight:800, fontSize:"0.8rem", color:"#93C5FD",
          }}>{String(index+1).padStart(2,"0")}</div>
          <div style={{fontWeight:700, fontSize:"0.9rem"}}>Offer {index+1}</div>
          {computedFactor > 0 && (
            <span className="chip" style={{background:"rgba(245,158,11,0.12)", color:"#FCD34D", borderColor:"rgba(245,158,11,0.3)"}}>
              {computedFactor.toFixed(2)}× factor
            </span>
          )}
          {apr > 0 && (
            <span className="chip" data-testid={`chip-apr-${index}`}>
              {apr.toFixed(2)}% APR
            </span>
          )}
        </div>
        <div style={{display:"flex", gap:4}}>
          <button className="btn btn-ghost btn-icon" onClick={onRecalc}
                  title="Recompute payment, factor and APR from funded + payback + term"
                  data-testid={`recalc-offer-${index}`}>
            <RotateCcw size={13}/>
          </button>
          <button className="btn btn-ghost btn-icon" onClick={onRemove} data-testid={`remove-offer-${index}`}>
            <Trash2 size={14}/>
          </button>
        </div>
      </div>

      <div style={{display:"grid", gridTemplateColumns:"1fr 1fr", gap:10}}>
        <MiniField label="Funded amount ($)" testid={`f-funding-${index}`}>
          <input className="input" value={offer.funding_amount ?? ""}
                 onChange={e=>onChange("funding_amount", e.target.value)} placeholder="100000"/>
        </MiniField>
        <MiniField label="Total payback ($)" testid={`f-payback-${index}`}>
          <input className="input" value={offer.total_payback ?? ""}
                 onChange={e=>onChange("total_payback", e.target.value)} placeholder="130000"/>
        </MiniField>

        <MiniField label="Payment ($)" testid={`f-payment-${index}`}>
          <input className="input" value={offer.fixed_amount ?? ""}
                 onChange={e=>onChange("fixed_amount", e.target.value)} placeholder="650"/>
        </MiniField>
        <MiniField label="Frequency">
          <select className="select" value={offer.frequency || "Daily"}
                  onChange={e=>onChange("frequency", e.target.value)} data-testid={`f-freq-${index}`}>
            {FREQ_OPTIONS.map(f => <option key={f}>{f}</option>)}
          </select>
        </MiniField>

        <MiniField label="Term (# of payments or '6 months')">
          <input className="input" value={offer.term || ""}
                 onChange={e=>onChange("term", e.target.value)}
                 placeholder="100 = 100 payments at the freq"
                 data-testid={`f-term-${index}`}/>
        </MiniField>
        <MiniField label="Position">
          <select className="select" value={offer.position || ""}
                  onChange={e=>onChange("position", e.target.value)} data-testid={`f-position-${index}`}>
            <option value="">—</option>
            {POSITION_OPTIONS.map(p => <option key={p}>{p}</option>)}
          </select>
        </MiniField>

        <MiniField label="Interest %" testid={`f-interest-${index}`}>
          <input className="input"
                 value={offer.interest_pct || (fa>0 && tp>0 ? ((tp/fa - 1) * 100).toFixed(2) : "")}
                 onChange={e=>onChange("interest_pct", e.target.value)}
                 placeholder="50 = 50% interest"/>
        </MiniField>
        <MiniField label="Factor rate" testid={`f-factor-${index}`}>
          <input className="input"
                 value={offer.factor_rate || (fa>0 && tp>0 ? (tp/fa).toFixed(3) : "")}
                 onChange={e=>onChange("factor_rate", e.target.value)} placeholder="1.30"/>
        </MiniField>

        <MiniField label="Origination / closing fee">
          <input className="input" value={offer.origination_fee || ""}
                 onChange={e=>onChange("origination_fee", e.target.value)} placeholder="2.50% or $2,500" data-testid={`f-fee-${index}`}/>
        </MiniField>

        <MiniField label="APR % (auto)">
          <input className="input" readOnly value={apr > 0 ? apr.toFixed(2) : ""} placeholder="auto"
                 style={{color:"var(--muted)", background:"rgba(0,0,0,0.25)"}}
                 data-testid={`f-apr-${index}`}/>
        </MiniField>
        <MiniField label="MRR % (auto)">
          <input className="input" readOnly value={mrr > 0 ? mrr.toFixed(2) : ""} placeholder="auto"
                 style={{color:"var(--muted)", background:"rgba(0,0,0,0.25)"}}
                 data-testid={`f-mrr-${index}`}/>
        </MiniField>
      </div>

      <div style={{marginTop:10}}>
        <div style={{fontSize:"0.66rem", fontWeight:700, letterSpacing:"0.06em", color:"var(--muted)", textTransform:"uppercase", marginBottom:3}}>
          Notes for this offer
        </div>
        <textarea className="textarea" rows={2}
                  value={offer.notes || ""}
                  placeholder="e.g. early-payoff discount available · ACH only · funds Monday"
                  data-testid={`f-notes-${index}`}
                  onChange={e=>onChange("notes", e.target.value)}/>
      </div>

      <div style={{fontSize:"0.7rem", color:"var(--muted)", marginTop:8}}>
        Type <b>Funded</b> → <b>Interest %</b> (or <b>Factor</b>) → <b>Frequency</b> → <b>Term</b>. Payment, Payback, APR &amp; MRR auto-calculate. Term = a bare number means "count of payments" at that frequency (e.g. 100 daily = 100 payments).
      </div>
    </div>
  );
}

function MiniField({ label, testid, children }) {
  return (
    <div data-testid={testid}>
      <div style={{fontSize:"0.66rem", fontWeight:700, letterSpacing:"0.06em", color:"var(--muted)", textTransform:"uppercase", marginBottom:3}}>
        {label}
      </div>
      {children}
    </div>
  );
}


function AiHelp() {
  return (
    <div data-testid="ai-help">
      <SectionTitle icon={<Sparkles size={14}/>} title="What the AI can do"/>
      <ul style={{margin:0, paddingLeft:"1.1rem", lineHeight:1.6, color:"var(--ink)", fontSize:"0.88rem"}}>
        <li>Change the <b>theme/colors</b> (pink, navy, emerald, royal, sunset, ocean, rose gold…)</li>
        <li>Swap the <b>decoration</b> on the cover (bow, gift box, confetti, art deco…)</li>
        <li>Rewrite the <b>tagline</b>, <b>sub-headline</b>, or <b>closing CTA</b></li>
        <li><b>Highlight</b> a specific offer as recommended</li>
        <li><b>Reorder, hide,</b> or <b>remove</b> offers</li>
        <li>Update your <b>name, phone, email</b>, or business name</li>
      </ul>
      <div style={{marginTop:"0.9rem", padding:"0.7rem", border:"1px solid var(--line)", borderRadius:10, background:"rgba(59,130,246,0.06)"}}>
        <div style={{fontSize:"0.78rem", color:"var(--accent-2)", fontWeight:700, marginBottom:4}}>Pro tip</div>
        <div style={{fontSize:"0.82rem", color:"var(--ink)"}}>
          Use the chat on the right and the preview updates instantly. Try “make it monochrome with a minimal line on the cover and recommend offer 13.”
        </div>
      </div>
    </div>
  );
}


function SectionTitle({ icon, title }) {
  return (
    <div style={{display:"flex", alignItems:"center", gap:6, margin:"0.9rem 0 0.5rem", color:"var(--ink-2)"}}>
      {icon}
      <span style={{fontSize:"0.72rem", fontWeight:800, textTransform:"uppercase", letterSpacing:"0.1em"}}>{title}</span>
      <div style={{flex:1, height:1, background:"linear-gradient(90deg, var(--line), transparent)", marginLeft:8}}/>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div style={{marginBottom:10}}>
      <label className="label">{label}</label>
      {children}
    </div>
  );
}

function DecoIcon({ name, theme }) {
  // tiny SVG previews for each decoration concept, colored to match the active theme
  const w = 70, h = 26;
  const common = { viewBox: `0 0 ${w} ${h}`, width: "100%", height: 28 };
  const sw = THEME_SWATCHES[theme] || THEME_SWATCHES.navy;
  const ribbon  = sw.accent;      // main bright accent
  const gold    = sw.accent2;     // secondary
  const soft    = sw.accent2;     // (fallback)
  const dark    = sw.primary;     // shadow / dark accent
  switch (name) {
    case "bow_ribbon":
      return (
        <svg {...common}>
          <rect x="0" y="9" width={w} height="8" fill={ribbon}/>
          <rect x="0" y="9" width={w} height="2" fill="#fff" opacity="0.45"/>
          <path d="M28,13 q-7,-8 -10,0 q7,8 10,0 z" fill={ribbon}/>
          <path d="M42,13 q7,-8 10,0 q-7,8 -10,0 z" fill={ribbon}/>
          <rect x="33" y="9" width="4" height="8" fill={dark}/>
        </svg>
      );
    case "ribbon_only":
      return (
        <svg {...common}>
          <rect x="0" y="9" width={w} height="8" fill={ribbon}/>
          <rect x="0" y="9" width={w} height="2" fill="#fff" opacity="0.45"/>
        </svg>
      );
    case "confetti":
      return (
        <svg {...common}>
          {[...Array(14)].map((_,i)=>(
            <rect key={i} x={i*5} y={5+(i%5)*3} width="3" height="3" fill={[ribbon,gold,soft][i%3]} transform={`rotate(${i*23} ${i*5+1.5} ${7})`}/>
          ))}
        </svg>
      );
    case "gift_box":
      return (
        <svg {...common}>
          <rect x="22" y="8" width="26" height="15" fill={ribbon}/>
          <rect x="22" y="8" width="26" height="3" fill={dark}/>
          <rect x="33" y="8" width="4" height="15" fill={gold}/>
          <path d="M30,8 q5,-7 10,0 z" fill={ribbon}/>
        </svg>
      );
    case "stars":
      return (
        <svg {...common}>
          {[12,30,48,60].map((x,i)=>(
            <polygon key={i} points={`${x},2 ${x+2},${10} ${x+10},13 ${x+2},16 ${x},24 ${x-2},16 ${x-10},13 ${x-2},10`} fill={[gold,ribbon,soft,gold][i]} />
          ))}
        </svg>
      );
    case "floral":
      return (
        <svg {...common}>
          <path d="M2,2 q12,8 20,22" stroke={ribbon} fill="none" strokeWidth="1.6"/>
          <path d="M68,2 q-12,8 -20,22" stroke={ribbon} fill="none" strokeWidth="1.6"/>
          <ellipse cx="14" cy="11" rx="5" ry="2" fill={gold}/>
          <ellipse cx="56" cy="11" rx="5" ry="2" fill={gold}/>
        </svg>
      );
    case "geometric":
      return (
        <svg {...common}>
          {[...Array(8)].map((_,i)=>(<polygon key={i} points={`${i*9},22 ${i*9+9},22 ${i*9+4.5},14`} fill={ribbon}/>))}
          {[8,24,40,56].map((x,i)=>(<circle key={i} cx={x} cy="6" r="3" fill={gold}/>))}
        </svg>
      );
    case "art_deco":
      return (
        <svg {...common}>
          {[3,6,9].map(y=>(<line key={y} x1="0" y1={y} x2={w} y2={y} stroke={gold} strokeWidth="0.8"/>))}
          {[10,30,50,68].map((x,i)=>(<polygon key={i} points={`${x-4},22 ${x},14 ${x+4},22 ${x},18`} fill={ribbon}/>))}
          {[19,22].map(y=>(<line key={y} x1="0" y1={y} x2={w} y2={y} stroke={gold} strokeWidth="0.6"/>))}
        </svg>
      );
    case "minimal":
      return (
        <svg {...common}>
          <rect x="0" y="12" width={w} height="3" fill={ribbon}/>
          <rect x="0" y="17" width={w} height="1" fill={gold}/>
        </svg>
      );
    default:
      return null;
  }
}
